import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Forum as ForumIcon,
  RecordVoiceOver as BroadcastIcon
} from '@mui/icons-material';
import { fetchSentMessages } from '../../redux/slices/driverMessagesSlice';
import brandingConfig from '../../config/brandingConfig';
import DriverMessageComposerSheet from './DriverMessageComposerSheet';

const formatMessageDate = (value) => {
  if (!value) return '';

  try {
    const date = value?.toDate ? value.toDate() : new Date(value);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Gerade eben';
    if (diffInMinutes < 60) return `vor ${diffInMinutes} Min`;
    if (diffInMinutes < 1440) return `vor ${Math.floor(diffInMinutes / 60)} Std`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return '';
  }
};

const DriverMessageCenter = ({ user }) => {
  const dispatch = useDispatch();
  const { sentMessages, loading } = useSelector((state) => state.driverMessages);

  useEffect(() => {
    if (user?.uid) {
      dispatch(fetchSentMessages({ limitCount: 10 }));
    }
  }, [dispatch, user?.uid]);

  const recentSentMessages = sentMessages?.slice(0, 4) || [];

  return (
    <>
      <Box sx={{ p: 2, backgroundColor: brandingConfig.colors.primary, color: 'primary.contrastText' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ForumIcon />
          Fahrer-Nachrichten
          {recentSentMessages.length > 0 && (
            <Chip
              label={recentSentMessages.length}
              size="small"
              sx={{
                backgroundColor: 'white',
                color: brandingConfig.colors.primary,
                fontWeight: 'bold'
              }}
            />
          )}
        </Typography>
      </Box>

      <DriverMessageComposerSheet />

      <Divider />

      <Box sx={{ px: 2, py: 1, backgroundColor: brandingConfig.colors.background }}>
        <Typography variant="subtitle2" color="text.secondary">
          Kürzlich gesendet
        </Typography>
      </Box>

      {recentSentMessages.length > 0 ? (
        recentSentMessages.map((message) => (
          <MenuItem key={message.id} disabled sx={{ opacity: '1 !important' }}>
            <ListItemIcon>
              {message.recipientId === 'all' ? (
                <BroadcastIcon sx={{ color: brandingConfig.colors.primary }} />
              ) : (
                <CheckCircleIcon sx={{ color: brandingConfig.colors.success }} />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {message.title}
                  </Typography>
                  {message.recipientId === 'all' && (
                    <Chip label="Alle" size="small" color="primary" />
                  )}
                  {message.priority === 'high' && (
                    <Chip label="Wichtig" size="small" color="warning" />
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography component="span" variant="body2" sx={{ display: 'block' }}>
                    {message.message?.length > 70 ? `${message.message.substring(0, 70)}...` : message.message}
                  </Typography>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {formatMessageDate(message.createdAt)}
                  </Typography>
                </>
              }
            />
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>
          <ListItemText
            primary={loading ? 'Nachrichten werden geladen...' : 'Noch keine gesendeten Nachrichten'}
            secondary="Neue Nachrichten können direkt oben gesendet werden."
          />
        </MenuItem>
      )}
    </>
  );
};

export default DriverMessageCenter;
